import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Flex, Text, GridItem, Spinner } from '@chakra-ui/react';
import { formatCurrency } from 'utils/CommonFunction';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
const NetRevenueGraph = ({
  intervalValue,
  revenueGraphData,
  currency,
  dashboardData,
  isLoading,
}) => {
  const StationOnlineBox = (
    <Flex
      flex="1"
      p="4"
      position="relative"
      gap="24px"
      align="center"
      borderRadius="20px"
      border="1px solid"
      borderColor="reviveOrange"
      bg="linear-gradient(0deg, rgba(251, 176, 59, 0.1), rgba(251, 176, 59, 0.1)), #FFFFFF"
      boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
      overflow="hidden"
      _after={{
        content: '""',
        position: 'absolute',
        width: '234px',
        height: '234px',
        right: '30px',
        bottom: '0',
        background:
          'linear-gradient(107.66deg, #FBB03B 12.13%, rgba(251, 176, 59, 0) 87.41%)',
        opacity: '0.18',
        transform: 'matrix(-1, 0, 0, 1, 0, 0)',
        borderRadius: '50%',
      }}
    >
      <Flex
        bg="reviveOrange"
        borderRadius="10px"
        height="73px"
        width="73px"
        flexShrink={0}
        align="center"
        justify="center"
      >
        <Box
          as="svg"
          width="37px"
          height="37px"
          viewBox="0 0 37 37"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="36"
            height="36"
            transform="translate(0.5 0.167969)"
            fill="#FBB03B"
          />
          <path
            d="M23 4.66797C22.9997 3.92214 22.7033 3.20694 22.1759 2.67956C21.6485 2.15218 20.9333 1.85577 20.1875 1.85547H6.6875C5.94167 1.85577 5.22647 2.15218 4.69909 2.67956C4.17171 3.20694 3.8753 3.92214 3.875 4.66797V30.543H23V4.66797ZM12.3125 28.293L13.2125 23.793H9.5L14.5625 17.043L13.6625 21.543H17.375L12.3125 28.293ZM19.625 14.2305H7.25V5.23047H19.625V14.2305ZM24.6875 32.793V34.4805H2.1875V32.793C2.1875 32.4946 2.30603 32.2084 2.517 31.9975C2.72798 31.7865 3.01413 31.668 3.3125 31.668H23.5625C23.8609 31.668 24.147 31.7865 24.358 31.9975C24.569 32.2084 24.6875 32.4946 24.6875 32.793ZM34.8125 5.79297V9.16797C34.8125 9.91389 34.5162 10.6293 33.9887 11.1567C33.4613 11.6842 32.7459 11.9805 32 11.9805H30.875C30.1291 11.9805 29.4137 11.6842 28.8863 11.1567C28.3588 10.6293 28.0625 9.91389 28.0625 9.16797V5.79297H34.8125ZM29.75 4.66797H28.625V1.85547C28.625 1.70628 28.6843 1.56321 28.7897 1.45772C28.8952 1.35223 29.0383 1.29297 29.1875 1.29297C29.3367 1.29297 29.4798 1.35223 29.5852 1.45772C29.6907 1.56321 29.75 1.70628 29.75 1.85547V4.66797ZM34.25 4.66797H33.125V1.85547C33.125 1.70628 33.1843 1.56321 33.2897 1.45772C33.3952 1.35223 33.5383 1.29297 33.6875 1.29297C33.8367 1.29297 33.9798 1.35223 34.0852 1.45772C34.1907 1.56321 34.25 1.70628 34.25 1.85547V4.66797ZM30.875 13.1055H32V26.6055C32 27.3514 31.7037 28.0668 31.1762 28.5942C30.6488 29.1216 29.9334 29.418 29.1875 29.418C28.4416 29.418 27.7262 29.1216 27.1988 28.5942C26.6713 28.0668 26.375 27.3514 26.375 26.6055V18.7305C26.3737 18.2833 26.1954 17.8549 25.8793 17.5387C25.5631 17.2225 25.1346 17.0443 24.6875 17.043H24.125V15.918H24.6875C25.4333 15.9183 26.1485 16.2147 26.6759 16.7421C27.2033 17.2694 27.4997 17.9846 27.5 18.7305V26.6055C27.5 27.053 27.6778 27.4822 27.9943 27.7987C28.3107 28.1152 28.7399 28.293 29.1875 28.293C29.635 28.293 30.0643 28.1152 30.3807 27.7987C30.6972 27.4822 30.875 27.053 30.875 26.6055V13.1055Z"
            fill="#203946"
          />
        </Box>
      </Flex>
      <Box>
        <Text
          fontSize={{ base: '24px', md: '40px' }}
          lineHeight="1.2"
          fontWeight="semibold"
          color="primary"
        >
          {dashboardData.onlineStation} / {dashboardData.totalStations}
        </Text>
        <Text fontSize={{ base: '10px', md: '16px' }} color="#292D32">
          Stations Online
        </Text>
      </Box>
    </Flex>
  );

  const PowerbankUsedBox = (
    <Flex
      flex="1"
      p="4"
      position="relative"
      gap="24px"
      align="center"
      borderRadius="20px"
      border="1px solid"
      borderColor="#53EB95"
      bg="linear-gradient(0deg, rgba(83, 235, 149, 0.1), rgba(83, 235, 149, 0.1)), #FFFFFF"
      boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
      overflow="hidden"
      _after={{
        content: '""',
        position: 'absolute',
        width: '234px',
        height: '234px',
        right: '30px',
        bottom: '0',
        background:
          'linear-gradient(107.66deg, #53EB95 12.13%, rgba(83, 235, 149, 0) 87.41%)',
        opacity: '0.18',
        transform: 'matrix(-1, 0, 0, 1, 0, 0)',
        borderRadius: '50%',
      }}
    >
      <Flex
        bg="#53EB95"
        borderRadius="10px"
        height="73px"
        width="73px"
        flexShrink={0}
        align="center"
        justify="center"
      >
        <Box
          as="svg"
          width="37px"
          height="37px"
          viewBox="0 0 35 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M34.3531 23.2172V25.7203C34.3531 26.5148 33.7625 27.1758 32.9961 27.2953V29.5453C32.9961 29.9531 32.6656 30.2766 32.2648 30.2766H29.5578C29.157 30.2766 28.8266 29.9531 28.8266 29.5453V27.2953C28.0602 27.1828 27.4695 26.5219 27.4695 25.7203V23.2172C27.4695 21.5719 28.6297 20.1938 30.1766 19.8562V5.00625C30.1766 3.05859 28.5875 1.46953 26.6398 1.46953H16.5922C15.0734 1.46953 13.7797 2.42578 13.2734 3.77578H11.7406C12.2891 1.61016 14.2578 0 16.5922 0H26.6398C29.3961 0 31.6461 2.24297 31.6461 5.00625V19.8562C33.193 20.1938 34.3531 21.5719 34.3531 23.2172ZM11.9727 20.5453L12.5352 17.7117L9.30078 22.4859H11.5367C11.9937 22.4859 12.3383 22.9008 12.2609 23.3508L11.7336 26.3602L15.2984 21.4242H12.6969C12.2328 21.4242 11.8812 21.0023 11.9727 20.5453Z"
            fill="#203946"
          />
          <path
            d="M21.007 7.68594H15.6141V7.40469C15.6141 6.21641 14.6508 5.24609 13.4555 5.24609H11.1914C10.0031 5.24609 9.03281 6.21641 9.03281 7.40469V7.68594H3.63281C1.9875 7.68594 0.644531 9.02891 0.644531 10.6742V33.0195C0.644531 34.6648 1.9875 36.0078 3.63281 36.0078H21.007C22.6523 36.0078 23.9953 34.6648 23.9953 33.0195V10.6672C23.9953 9.02187 22.6523 7.68594 21.007 7.68594ZM17.3227 21.1156L11.0437 29.8133C10.8469 30.0875 10.4742 30.193 10.1648 30.0594C9.84844 29.9258 9.66562 29.5953 9.72891 29.2578L10.6641 23.9562H7.91484C7.64062 23.9562 7.39453 23.8086 7.26797 23.5625C7.14141 23.3164 7.15547 23.0281 7.31016 22.8031L13.3641 13.8805C13.5609 13.5922 13.9336 13.4797 14.257 13.6133C14.5805 13.7469 14.7633 14.0914 14.693 14.4359L13.5961 19.9484H16.725C16.9992 19.9484 17.2523 20.1031 17.3789 20.3492C17.5055 20.6023 17.4844 20.8977 17.3227 21.1156Z"
            fill="#203946"
          />
        </Box>
      </Flex>
      <Box>
        <Text
          fontSize={{ base: '24px', md: '40px' }}
          lineHeight="1.2"
          fontWeight="semibold"
          color="primary"
        >
          {dashboardData.powerbankOnRent}
        </Text>
        <Text fontSize={{ base: '10px', md: '16px' }} color="#292D32">
          Powerbanks Used
        </Text>
      </Box>
    </Flex>
  );

  return (
    <>
      <GridItem colSpan="12">
        <Box
          p="5"
          bg="white"
          boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
          borderRadius="20px"
          w="full"
          h="full"
          display="flex"
          flexWrap={'wrap'}
          flexDirection={{ base: 'column', xl: 'row' }}
          gap="24px"
        >
          <Text
            width="100%"
            fontSize={{ base: '20px', md: '24px' }}
            fontWeight="semibold"
          >
            Today
          </Text>

          {/* Left side - Net Revenue Chart */}
          <Box
            flex={{ base: '1', xl: '8' }}
            w="full"
            p={{ base: '4', md: '5' }}
            bg="white"
            border="1px solid #D9D9D9"
            borderRadius="20px"
            boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          >
            {/* Title & Amount */}
            <Text
              fontSize={{ base: '20px', md: '24px' }}
              fontWeight="semibold"
              mb="16px"
            >
              Net Volume
            </Text>
            {isLoading ? (
              <>
                <Flex justify="center" alignItems="center" height="300px">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              </>
            ) : (
              <>
                <Text
                  fontSize="32px"
                  color="primary"
                  fontWeight="medium"
                  mb="24px"
                >
                  {formatCurrency(revenueGraphData.totalRevenue, currency)}
                </Text>
                {/* Chart Container */}
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart
                    data={revenueGraphData?.revenueData}
                    margin={{ top: 20, left: 0 }}
                    accessibilityLayer
                  >
                    <XAxis
                      dataKey="time"
                      scale="point"
                      interval={intervalValue}
                      padding={{ left: 10, right: 10 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => formatCurrency(value, currency)}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrency(value, currency)}
                      labelFormatter={(label) => `${label}`}
                    />
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#53EB95"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 8 }}
                    ></Line>
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Box>

          {/* Right side - Stats Cards */}
          <Box
            flex={{ base: '1', xl: '4' }}
            display="flex"
            flexDirection="column"
            gap="32px"
          >
            {/* Stations Online */}
            <Link to="/admin/stations" state={{ status: 'offline' }}>
              {StationOnlineBox}
            </Link>

            {/* Powerbanks Used */}
            <Link to="/admin/orders">{PowerbankUsedBox}</Link>
          </Box>
        </Box>
      </GridItem>
    </>
  );
};

NetRevenueGraph.propTypes = {
  intervalValue: PropTypes.number,
  revenueGraphData: PropTypes.any,
  currency: PropTypes.string,
  dashboardData: PropTypes.any,
  isLoading: PropTypes.bool,
};

export default NetRevenueGraph;
